// useCanvasDrag — drag-motorn för WorkflowCanvas: block-, collapsed-
// group-, group-frame- och waypoint-drag. Extraherad ur
// WorkflowCanvas.tsx (#2 i PRE_RELEASE_REVIEW_100, slice 2).
//
// Äger:
//   • isDragging / dragBlockId-state + speglande refs (touch-pathen
//     läser refs eftersom event-listeners inte re-binds per render)
//   • dragOffsetRef (offset i ref, inte state — first-frame-race:
//     mouseMove kan fira innan setState committat)
//   • groupDrag-init-refs (samma race på touch: move fires ~1 RAF
//     efter start, innan React committat state)
//   • waypoint-drag-state + handlers
//   • mousemove/mouseup-drag-loopen (block / group: / collapsed:)
//   • orientation-change/visibility-abort
//
// Drag-mathen bor i libs (computeGroupDragUpdates, resolveGroupOverlap,
// proposedGroupBounds) — hooken är glue.

import { useState, useRef, useCallback, useEffect, type RefObject } from 'react';
import type { WorkflowBlock as WorkflowBlockType, BlockGroup } from '@/types/workflow';
import { type Point } from '@/lib/connectorUtils';
import { resolveGroupOverlap } from '@/lib/overlapResolver';
import { computeGroupDragUpdates, proposedGroupBounds } from '@/lib/groupDragMath';
import type { computeDynamicCanvasSize } from '@/lib/canvasBounds';

interface UseCanvasDragArgs {
  canvasRef: RefObject<HTMLDivElement>;
  viewOnly: boolean;
  blocks: WorkflowBlockType[];
  groups: BlockGroup[];
  contentScale: number;
  computeBounds: () => ReturnType<typeof computeDynamicCanvasSize>;
  frozenCanvasBoundsRef: React.MutableRefObject<ReturnType<typeof computeDynamicCanvasSize> | null>;
  getOrigin: () => { x: number; y: number };
  onUpdateBlockPosition: (id: string, x: number, y: number, moveGroup?: boolean) => void;
  onUpdateGroupPosition: (id: string, x: number, y: number) => void;
  onUpdateConnectorWaypoints: (id: string, waypoints: Point[]) => void;
  onSelectGroup: (id: string | null) => void;
  /** Force-re-render efter bounds-thaw så liveBounds tas i bruk direkt. */
  onBoundsThawed: () => void;
}

export function useCanvasDrag({
  canvasRef,
  viewOnly,
  blocks,
  groups,
  contentScale,
  computeBounds,
  frozenCanvasBoundsRef,
  getOrigin,
  onUpdateBlockPosition,
  onUpdateGroupPosition,
  onUpdateConnectorWaypoints,
  onSelectGroup,
  onBoundsThawed,
}: UseCanvasDragArgs) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragBlockId, setDragBlockId] = useState<string | null>(null);
  // Group-drag init-positions + start-mouse are stored in REFS, not
  // React state. The touch path fires onGroupDragStart and the first
  // onGroupDragMove within ~16 ms (one RAF tick) — React 18 hasn't
  // committed a setState from the start handler yet by the time the
  // move handler runs, so blocks would see an empty `{}` for the
  // first 1-2 frames and effectively skip their update. The result on
  // mobile is the classic "drag the big group → it visibly jumps back,
  // then catches up" stutter. Mirrors the same fix already in place
  // for block-drag's `dragOffsetRef`.
  const groupDragInitialPositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const groupDragStartMouseRef = useRef({ x: 0, y: 0 });
  // Manuell waypoint-drag state. Auto-injection från AI/cloud är
  // strippad — det enda sättet att skapa waypoints är via dubbelklick
  // på linjen, och de här handlers driver drag-redigeringen.
  const [waypointDrag, setWaypointDrag] = useState<{
    connectorId: string;
    index: number;
    waypoints: Point[];
  } | null>(null);

  const isDraggingRef = useRef(false);
  const dragBlockIdRef = useRef<string | null>(null);
  // dragOffset lever bara i ref — state-versionen togs bort eftersom
  // mouseMove-läsningen från state introducerade en first-frame-race
  // (state inte committed innan första mouseMove → blocket snap-hoppade
  // till mus-positionen som om klicket var exakt block-center).
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const handleBlockDragStart = useCallback((blockId: string, e: React.MouseEvent) => {
    if (viewOnly) return;
    const blockElement = document.querySelector(`[data-block-id="${blockId}"]`) as HTMLElement;
    if (!blockElement) return;
    const blockRect = blockElement.getBoundingClientRect();
    const offset = {
      x: (e.clientX - (blockRect.left + blockRect.width / 2)) / contentScale,
      y: (e.clientY - (blockRect.top + blockRect.height / 2)) / contentScale,
    };
    // Frys canvas-bbox vid drag-start. Origin hoppar inte mid-drag →
    // drag-mathen är stabil och scenen "drifter" inte under cursorn.
    frozenCanvasBoundsRef.current = computeBounds();
    setIsDragging(true);
    setDragBlockId(blockId);
    isDraggingRef.current = true;
    dragBlockIdRef.current = blockId;
    dragOffsetRef.current = offset;
  }, [viewOnly, contentScale, computeBounds, frozenCanvasBoundsRef]);

  const handleCollapsedGroupDragStart = useCallback((groupId: string, e: React.MouseEvent) => {
    if (viewOnly) return;
    const el = document.querySelector(`[data-group-card-id="${groupId}"]`) as HTMLElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const id = `collapsed:${groupId}`;
    const offset = {
      x: (e.clientX - (rect.left + rect.width / 2)) / contentScale,
      y: (e.clientY - (rect.top + rect.height / 2)) / contentScale,
    };
    frozenCanvasBoundsRef.current = computeBounds();
    setIsDragging(true); setDragBlockId(id);
    isDraggingRef.current = true; dragBlockIdRef.current = id; dragOffsetRef.current = offset;
  }, [viewOnly, contentScale, computeBounds, frozenCanvasBoundsRef]);

  const handleAddWaypoint = useCallback((connectorId: string, point: Point, insertIndex: number, currentWaypoints: Point[]) => {
    const newWaypoints = [...currentWaypoints];
    newWaypoints.splice(insertIndex, 0, point);
    onUpdateConnectorWaypoints(connectorId, newWaypoints);
  }, [onUpdateConnectorWaypoints]);

  const handleRemoveWaypoint = useCallback((connectorId: string, waypointIndex: number, currentWaypoints: Point[]) => {
    const newWaypoints = currentWaypoints.filter((_, i) => i !== waypointIndex);
    onUpdateConnectorWaypoints(connectorId, newWaypoints);
  }, [onUpdateConnectorWaypoints]);

  const handleWaypointDragStart = useCallback((connectorId: string, waypointIndex: number, currentWaypoints: Point[], e: React.MouseEvent) => {
    e.stopPropagation();
    setWaypointDrag({ connectorId, index: waypointIndex, waypoints: currentWaypoints });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (waypointDrag && canvasRef.current) {
      const canvasInner = canvasRef.current.querySelector('.canvas-inner') as HTMLElement;
      if (!canvasInner) return;
      const innerRect = canvasInner.getBoundingClientRect();
      const origin = getOrigin();
      const x = (e.clientX - innerRect.left) / contentScale - origin.x;
      const y = (e.clientY - innerRect.top) / contentScale - origin.y;
      const newWaypoints = waypointDrag.waypoints.map((wp, i) => i === waypointDrag.index ? { x, y } : wp);
      setWaypointDrag(prev => prev ? { ...prev, waypoints: newWaypoints } : null);
      onUpdateConnectorWaypoints(waypointDrag.connectorId, newWaypoints);
      return;
    }
    if (!isDragging || !dragBlockId || !canvasRef.current) return;
    const canvasInner = canvasRef.current.querySelector('.canvas-inner') as HTMLElement;
    if (!canvasInner) return;
    const innerRect = canvasInner.getBoundingClientRect();
    const currentX = (e.clientX - innerRect.left) / contentScale;
    const currentY = (e.clientY - innerRect.top) / contentScale;
    if (dragBlockId.startsWith('group:')) {
      const groupId = dragBlockId.replace('group:', '');
      const group = groups.find(g => g.id === groupId);
      if (group) {
        const dragInput = {
          groupBlockIds: group.blockIds,
          initialPositions: groupDragInitialPositionsRef.current,
          startMouse: groupDragStartMouseRef.current,
          current: { x: currentX, y: currentY },
        };
        const bounds = group.boundsLeft != null ? proposedGroupBounds(dragInput) : null;
        const overlapAdjust = bounds
          ? resolveGroupOverlap(groupId, bounds, groups)
          : undefined;
        for (const u of computeGroupDragUpdates({ ...dragInput, overlapAdjust })) {
          onUpdateBlockPosition(u.blockId, u.x, u.y);
        }
      }
    } else if (dragBlockId.startsWith('collapsed:')) {
      const groupId = dragBlockId.replace('collapsed:', '');
      const origin = getOrigin();
      const dx = dragOffsetRef.current.x;
      const dy = dragOffsetRef.current.y;
      onUpdateGroupPosition(groupId, currentX - dx - origin.x, currentY - dy - origin.y);
    } else {
      const origin = getOrigin();
      // Läs dragOffset från REF, inte state. State-setter är async och
      // första mouseMove kan fira INNAN render-en med nytt state hinner
      // commit:a — då skulle offsetet vara {0,0} och blocket snap-hoppa
      // till musens position som om klick-punkten var exakt block-center
      // (vilket för tall-blocks visuellt ser ut som "insta dragen uppåt"
      // eftersom user typiskt klickar i header-området, ovanför centrum).
      const dx = dragOffsetRef.current.x;
      const dy = dragOffsetRef.current.y;
      const reqX = currentX - dx - origin.x;
      const reqY = currentY - dy - origin.y;
      // Direkt placering under drag — INGEN overlap-resolver. Spiral-
      // sökens kandidat-ordning (±x, 0) → (0, ±y) hittade fel-axel-
      // displacement vid tall-blocks → vertikal-låsning. Drag följer
      // musen 1:1; ev. överlapp löser user manuellt.
      onUpdateBlockPosition(dragBlockId, reqX, reqY, true);
    }
  }, [canvasRef, isDragging, dragBlockId, contentScale, onUpdateBlockPosition, onUpdateGroupPosition, groups, getOrigin, waypointDrag, onUpdateConnectorWaypoints]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false); setDragBlockId(null); setWaypointDrag(null);
    isDraggingRef.current = false; dragBlockIdRef.current = null;
    // Tina canvas-bbox så nästa render använder den live-räknade
    // versionen och växer för det nya block-läget.
    if (frozenCanvasBoundsRef.current) {
      frozenCanvasBoundsRef.current = null;
      // Force a re-render så liveBounds tas i bruk direkt (annars
      // måste något annat trigga re-render innan canvas växer).
      onBoundsThawed();
    }
  }, [frozenCanvasBoundsRef, onBoundsThawed]);

  // Abort any in-progress drag when the device rotates. Without this, the
  // block stays in a "being-dragged" state after orientation change — the
  // pointerup that would normally finish it is often swallowed by the
  // browser's rotation transition on iOS. Also cancel on page visibility
  // change so a switch-away + switch-back doesn't leave a stale drag.
  useEffect(() => {
    const cancel = () => {
      setIsDragging(false);
      setDragBlockId(null);

      isDraggingRef.current = false;
      dragBlockIdRef.current = null;
    };
    window.addEventListener('orientationchange', cancel);
    document.addEventListener('visibilitychange', () => { if (document.hidden) cancel(); });
    return () => {
      window.removeEventListener('orientationchange', cancel);
      document.removeEventListener('visibilitychange', cancel);
    };
  }, []);

  const handleGroupDragStart = useCallback((groupId: string, e: React.MouseEvent | React.PointerEvent) => {
    if (viewOnly) return;
    const canvasInner = canvasRef.current?.querySelector('.canvas-inner') as HTMLElement;
    if (!canvasInner) return;
    const innerRect = canvasInner.getBoundingClientRect();
    const mouseX = (e.clientX - innerRect.left) / contentScale;
    const mouseY = (e.clientY - innerRect.top) / contentScale;
    const group = groups.find(g => g.id === groupId);
    if (group) {
      const initialPositions: Record<string, { x: number; y: number }> = {};
      group.blockIds.forEach(blockId => {
        const block = blocks.find(b => b.id === blockId);
        if (block) initialPositions[blockId] = { x: block.x, y: block.y };
      });
      // Write refs SYNCHRONOUSLY so the next touchmove (which may fire
      // ~1 RAF later) reads a populated initial-positions map.
      groupDragInitialPositionsRef.current = initialPositions;
      groupDragStartMouseRef.current = { x: mouseX, y: mouseY };
    }
    frozenCanvasBoundsRef.current = computeBounds();
    const id = `group:${groupId}`;
    setIsDragging(true); setDragBlockId(id);
    isDraggingRef.current = true; dragBlockIdRef.current = id;
  }, [canvasRef, viewOnly, contentScale, groups, blocks, computeBounds, frozenCanvasBoundsRef]);

  // Touch-pathens group-drag-init: använder screen-koordinater istället
  // för ett React-event. Återanvänder samma init-logik som mouse-pathen
  // via ett syntetiskt event-objekt med bara clientX/Y.
  const handleGroupDragStartFromCoords = useCallback((groupId: string, screenX: number, screenY: number) => {
    handleGroupDragStart(groupId, { clientX: screenX, clientY: screenY } as React.MouseEvent);
    onSelectGroup(groupId);
  }, [handleGroupDragStart, onSelectGroup]);

  // Touch-pathens group-drag-move: deltakalkyl identisk med
  // handleMouseMove:s `dragBlockId.startsWith('group:')`-gren. Anropas
  // från useCanvasTouchGestures med currentX/Y i canvas-inner-coord.
  const handleGroupDragMoveFromCoords = useCallback((groupId: string, currentX: number, currentY: number) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    const dragInput = {
      groupBlockIds: group.blockIds,
      initialPositions: groupDragInitialPositionsRef.current,
      startMouse: groupDragStartMouseRef.current,
      current: { x: currentX, y: currentY },
    };
    const bounds = group.boundsLeft != null ? proposedGroupBounds(dragInput) : null;
    const overlapAdjust = bounds
      ? resolveGroupOverlap(groupId, bounds, groups)
      : undefined;
    for (const u of computeGroupDragUpdates({ ...dragInput, overlapAdjust })) {
      onUpdateBlockPosition(u.blockId, u.x, u.y);
    }
  }, [groups, onUpdateBlockPosition]);

  return {
    isDragging,
    setIsDragging,
    dragBlockId,
    setDragBlockId,
    isDraggingRef,
    dragBlockIdRef,
    dragOffsetRef,
    handleBlockDragStart,
    handleCollapsedGroupDragStart,
    handleAddWaypoint,
    handleRemoveWaypoint,
    handleWaypointDragStart,
    handleMouseMove,
    handleMouseUp,
    handleGroupDragStart,
    handleGroupDragStartFromCoords,
    handleGroupDragMoveFromCoords,
  };
}
